// src/contexts/StripePlansContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getPricePlans as fetchPricePlansService } from '../services/stripeService'; 

const StripePlansContext = createContext({
  allPlans: [],
  loadingPlans: true,
  errorPlans: null,
  plansFetched: false,
  fetchPlans: async () => { console.warn("fetchPlans called on default context implementation"); },
  getPlanById: (priceId) => null,
});

export const useStripePlans = () => useContext(StripePlansContext);

export const StripePlansProvider = ({ children }) => {
  const [allPlans, setAllPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false); 
  const [errorPlans, setErrorPlans] = useState(null);
  const [plansFetched, setPlansFetched] = useState(false); 

  const fetchPlans = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && plansFetched && !errorPlans) return;
    if (loadingPlans && !forceRefresh) return;

    console.log('[StripePlansContext] Fetching Stripe plans...');
    setLoadingPlans(true);
    setErrorPlans(null); 
    try {
      const response = await fetchPricePlansService();
      
      // The backend now sends a clean { plans: [...] } object.
      // We trust the backend. No more complex frontend logic.
      if (response && Array.isArray(response.plans)) {
        setAllPlans(response.plans);
        console.log(`[StripePlansContext] Plans fetched successfully: ${response.plans.length}`);
      } else {
        console.error('[StripePlansContext] Malformed response:', response);
        throw new Error('Failed to load plans or plans data is malformed.');
      }
    } catch (err) {
      console.error('[StripePlansContext] Error fetching pricing plans:', err);
      setErrorPlans(err.message || 'Could not fetch pricing plans.');
      setAllPlans([]); 
    } finally {
      setLoadingPlans(false);
      setPlansFetched(true); 
    }
  }, [plansFetched, errorPlans, loadingPlans]);

  useEffect(() => {
    if (!plansFetched && !loadingPlans) { 
        fetchPlans();
    }
  }, [fetchPlans, plansFetched, loadingPlans]);

  const getPlanById = useCallback((id) => {
    if (!allPlans || allPlans.length === 0 || !id) return null;
    // This now correctly searches the flat array for the priceId.
    return allPlans.find(p => p.priceId === id);
  }, [allPlans]);

  const value = {
    allPlans,
    loadingPlans,
    errorPlans,
    plansFetched,
    fetchPlans,
    getPlanById,
  };

  return (
    <StripePlansContext.Provider value={value}>
      {children}
    </StripePlansContext.Provider>
  );
};